import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowSquareOut, FileArrowUp, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { api } from "@/lib/api";
import { ADMIN } from "@/lib/routes";
import {
  cardholderDisplayName,
  formatCardholderContact,
} from "@/lib/cardholderContact";

const ID_TYPES = [
  { v: "0", l: "National ID card" },
  { v: "1", l: "Passport" },
  { v: "2", l: "Passport + visa" },
  { v: "3", l: "Driver license" },
];

const NEEDS_BACK = new Set(["0", "3"]);

const emptyForm = {
  countryArea: "",
  firstNameEn: "",
  lastNameEn: "",
  birthday: "",
  identityCardType: "1",
  identityCard: "",
  identityCardValidityTime: "",
  cardHasNoExpiry: false,
};

/** Admin console — cardholder KYC review and support submission. */
export default function CardholderKyc() {
  const { userId } = useParams();
  const base = "/admin/users";

  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [livenessSession, setLivenessSession] = useState(null);

  const load = useCallback(async () => {
    try {
      const kycRes = await api.get(`${base}/${userId}/kyc/status`);
      const u = kycRes.data.user || { id: userId };
      setUser(u);
      setStatus(kycRes.data);
      setForm((f) => ({
        ...f,
        firstNameEn: u.first_name || "",
        lastNameEn: u.last_name || "",
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not load cardholder");
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!frontFile) {
      toast.error("Front ID image is required");
      return;
    }
    if (NEEDS_BACK.has(form.identityCardType) && !backFile) {
      toast.error("Back image is required for this document type");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "cardHasNoExpiry") fd.append(k, v ? "true" : "false");
        else if (v) fd.append(k, v);
      });
      fd.append("frontImage", frontFile);
      if (backFile) fd.append("backImage", backFile);
      await api.post(`${base}/${userId}/kyc/submit-documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("KYC submitted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const openLiveness = async () => {
    try {
      const r = await api.get(`${base}/${userId}/kyc/liveness-url`);
      if (r.data?.accountId && r.data?.transactionId) {
        setLivenessSession({
          accountId: r.data.accountId,
          transactionId: r.data.transactionId,
        });
      }
      if (r.data?.url) window.open(r.data.url, "_blank", "noopener,noreferrer");
      else toast.error("No liveness URL returned");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not get liveness URL");
    }
  };

  const refreshLiveness = async () => {
    if (!livenessSession?.accountId || !livenessSession?.transactionId) {
      toast.error("Open liveness first to obtain accountId and transactionId");
      return;
    }
    try {
      const r = await api.get(`${base}/${userId}/kyc/liveness-status`, {
        params: {
          accountId: livenessSession.accountId,
          transactionId: livenessSession.transactionId,
        },
      });
      const issuer = r.data;
      const audit = issuer?.data?.auditStatus;
      const code = issuer?.code;
      toast.message(
        code != null
          ? `Liveness status (code ${code}${audit != null ? `, auditStatus ${audit}` : ""})`
          : "Liveness status received",
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check liveness");
    }
  };

  const kycStatus = status?.kyc_status || user?.kyc_status || "not_submitted";
  const canSubmit = !["submitted", "approved"].includes(kycStatus);
  const contact = formatCardholderContact(user);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="cardholder-kyc-page">
        <Link to={ADMIN.users} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3">
          <ArrowLeft size={12} /> Cardholders
        </Link>

        <PageHeader
          title={cardholderDisplayName(user) || "Cardholder"}
          subtitle={contact !== "—" ? contact : userId}
          actions={<StatusPill value={kycStatus} />}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Surface className="p-4">
            <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Level 1</div>
            <div className="text-sm font-medium capitalize">{kycStatus.replace(/_/g, " ")}</div>
            {status?.kyc_submitted_at && (
              <div className="text-xs text-zinc-500 mt-1">Submitted {status.kyc_submitted_at.slice(0, 10)}</div>
            )}
            {status?.kyc_approved_at && (
              <div className="text-xs text-zinc-500 mt-1">Approved {status.kyc_approved_at.slice(0, 10)}</div>
            )}
            {status?.kyc_rejection_reason && (
              <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-sm p-2">
                {status.kyc_rejection_reason}
              </div>
            )}
          </Surface>

          <Surface className="p-4">
            <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1">Level 2 (liveness)</div>
            <div className="text-sm font-medium">{status?.level2_status || "—"}</div>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={openLiveness}
                disabled={kycStatus === "not_submitted"}
                className="h-8 px-3 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 disabled:opacity-40 inline-flex items-center gap-1"
              >
                <ArrowSquareOut size={12} /> Open liveness
              </button>
              <button
                type="button"
                onClick={refreshLiveness}
                className="h-8 px-3 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 inline-flex items-center gap-1"
              >
                <ArrowsClockwise size={12} /> Refresh
              </button>
            </div>
          </Surface>

          <Surface className="p-4 text-xs text-zinc-600 leading-relaxed">
            <p className="font-medium text-zinc-900 mb-1">Flow</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Submit identity documents (Level 1)</li>
              <li>Complete face verification (Level 2)</li>
              <li>Issuer reviews and sends webhook updates</li>
            </ol>
          </Surface>
        </div>

        {canSubmit ? (
          <Surface className="p-5 max-w-2xl">
            <h2 className="font-display text-lg tracking-tight font-medium mb-4">Submit KYC documents</h2>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" value={form.firstNameEn} onChange={(v) => setForm({ ...form, firstNameEn: v })} />
                <Field label="Last name" value={form.lastNameEn} onChange={(v) => setForm({ ...form, lastNameEn: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Country (ISO-2)" value={form.countryArea} onChange={(v) => setForm({ ...form, countryArea: v.toUpperCase() })} maxLength={2} />
                <Field label="Date of birth" type="date" value={form.birthday} onChange={(v) => setForm({ ...form, birthday: v })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">ID type</label>
                  <select
                    value={form.identityCardType}
                    onChange={(e) => setForm({ ...form, identityCardType: e.target.value })}
                    className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-sm"
                  >
                    {ID_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                  </select>
                </div>
                <Field label="ID number" value={form.identityCard} onChange={(v) => setForm({ ...form, identityCard: v })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.cardHasNoExpiry}
                  onChange={(e) => setForm({ ...form, cardHasNoExpiry: e.target.checked })}
                />
                Document has no expiry date
              </label>
              {!form.cardHasNoExpiry && (
                <Field label="Expiry date" type="date" value={form.identityCardValidityTime} onChange={(v) => setForm({ ...form, identityCardValidityTime: v })} />
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Front image *</label>
                <input type="file" accept="image/*" onChange={(e) => setFrontFile(e.target.files?.[0] || null)} className="w-full text-sm" />
              </div>
              {NEEDS_BACK.has(form.identityCardType) && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Back image *</label>
                  <input type="file" accept="image/*" onChange={(e) => setBackFile(e.target.files?.[0] || null)} className="w-full text-sm" />
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                className="h-9 px-4 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <FileArrowUp size={14} /> {busy ? "Submitting…" : "Submit KYC"}
              </button>
            </form>
          </Surface>
        ) : (
          <Surface className="p-5 text-sm text-zinc-600">
            KYC is <span className="font-medium text-zinc-900">{kycStatus}</span>. Resubmission is disabled unless the issuer rejects the application.
          </Surface>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, value, onChange, type = "text", maxLength }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 text-sm border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 outline-none"
      />
    </div>
  );
}
